import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Box, Typography } from '@mui/material';

interface EquityChartProps {
  data: { date: string; value: number }[];
}

/**
 * 净值曲线图
 * 使用 Recharts 展示账户净值变化
 */
export default function EquityChart({ data }: EquityChartProps) {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">暂无净值数据</Typography>
      </Box>
    );
  }

  // 计算初始值作为基准线
  const initialValue = data[0]?.value ?? 0;

  // 格式化 Y 轴
  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toFixed(0);
  };

  // 格式化 Tooltip
  const formatTooltip = (value: number, name: string) => {
    return [`¥${value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, '账户净值'];
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11 }}
          tickFormatter={(val) => val.substring(5)}
        />
        <YAxis
          tick={{ fontSize: 11 }}
          tickFormatter={formatYAxis}
          domain={['auto', 'auto']}
        />
        <Tooltip
          formatter={formatTooltip}
          labelFormatter={(label) => `日期: ${label}`}
          contentStyle={{
            borderRadius: 8,
            border: '1px solid #e0e0e0',
            fontSize: 13,
          }}
        />
        <ReferenceLine y={initialValue} stroke="#9e9e9e" strokeDasharray="5 5" label="初始资金" />
        <Line
          type="monotone"
          dataKey="value"
          stroke="#1565c0"
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
